// Proxy Contest "Form Status" state machine — pure, dependency-free.
// The signature-validation rule lives here: a solicitor-signed form is always
// rejected/re-routed, never accepted as RECEIVED.
import type { ContestFormStatus } from '../data/mockProxyJobs';

export type ContestFormEvent =
  | { type: 'SEND' }
  | { type: 'SIGNATURE_RECEIVED'; signedBy: 'dissident' | 'solicitor' }
  | { type: 'MARK_RECEIVED' }
  | { type: 'SEND_TO_DISCOVERY' };

export interface TransitionResult {
  next: ContestFormStatus;
  ok: boolean;
  reason: string;
}

export const FORM_STATUS_LABEL: Record<ContestFormStatus, string> = {
  NOT_SENT: 'Not Sent',
  SENT: 'Sent',
  SIGNED_DISSIDENT: 'Signed by Dissident',
  SIGNED_SOLICITOR_REJECTED: 'Signed by Solicitor: Rejected',
  RECEIVED: 'Received',
  SENT_TO_DISCOVERY: 'Sent to Discovery',
};

export function transitionFormStatus(current: ContestFormStatus, event: ContestFormEvent): TransitionResult {
  if (event.type === 'SEND' && (current === 'NOT_SENT' || current === 'SIGNED_SOLICITOR_REJECTED')) {
    return { next: 'SENT', ok: true, reason: current === 'SIGNED_SOLICITOR_REJECTED' ? 'Re-routed to solicitor for dissident signature.' : 'Contest outreach sent.' };
  }
  if (event.type === 'SIGNATURE_RECEIVED' && current === 'SENT') {
    if (event.signedBy === 'solicitor') {
      return { next: 'SIGNED_SOLICITOR_REJECTED', ok: true, reason: 'Solicitor signature is invalid, so the form was re-routed to the solicitor for dissident signature.' };
    }
    return { next: 'SIGNED_DISSIDENT', ok: true, reason: 'Signed by the dissident party, so it\'s valid.' };
  }
  if (event.type === 'MARK_RECEIVED' && current === 'SIGNED_DISSIDENT') {
    return { next: 'RECEIVED', ok: true, reason: 'Signed form received.' };
  }
  if (event.type === 'SEND_TO_DISCOVERY' && current === 'RECEIVED') {
    return { next: 'SENT_TO_DISCOVERY', ok: true, reason: 'Sent to Discovery for BOC upload.' };
  }
  return { next: current, ok: false, reason: `Cannot apply ${event.type} to a contest in status "${FORM_STATUS_LABEL[current]}".` };
}

export function nextActionsFor(status: ContestFormStatus): { label: string; event: ContestFormEvent }[] {
  switch (status) {
    case 'NOT_SENT':
      return [{ label: 'Send Contest Outreach', event: { type: 'SEND' } }];
    case 'SENT':
      return [
        { label: 'Mark Signed by Dissident', event: { type: 'SIGNATURE_RECEIVED', signedBy: 'dissident' } },
        { label: 'Mark Signed by Solicitor', event: { type: 'SIGNATURE_RECEIVED', signedBy: 'solicitor' } },
      ];
    case 'SIGNED_SOLICITOR_REJECTED':
      return [{ label: 'Re-send Contest Outreach', event: { type: 'SEND' } }];
    case 'SIGNED_DISSIDENT':
      return [{ label: 'Mark Received', event: { type: 'MARK_RECEIVED' } }];
    case 'RECEIVED':
      return [{ label: 'Send to Discovery', event: { type: 'SEND_TO_DISCOVERY' } }];
    case 'SENT_TO_DISCOVERY':
      return [];
  }
}

export const STANDARD_CONTEST_FEE = 5000;

export function isFeeDiscounted(fee: number, standardFee: number = STANDARD_CONTEST_FEE): boolean {
  return fee < standardFee;
}
